import { COLLECTION, type CollectionItem } from "@/lib/collection";
import type { Tables } from "@/integrations/supabase/types";

export const PHOTO_BUCKET = "collection-photos";
export const MAX_PHOTO_BYTES = 12 * 1024 * 1024;

export const CRAFT_OPTIONS = [
  "Handmade",
  "Crocheted by hand",
  "Knit by hand",
  "Sewn by hand",
  "Crocheted and sewn by hand",
  "Knit and sewn by hand",
] as const;

export type CollectionPieceRow = Tables<"collection_pieces">;

const RESERVED_SLUGS = new Set(COLLECTION.map((item) => item.slug));

export function slugifyPieceName(name: string) {
  const base = name
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60)
    .replace(/-+$/g, "");
  return base || "piece";
}

export function isReservedSlug(slug: string) {
  return RESERVED_SLUGS.has(slug);
}

export function publicPhotoUrl(path: string) {
  const encoded = path
    .split("/")
    .map((part) => encodeURIComponent(part))
    .join("/");
  return `/api/public/photos/${encoded}`;
}

export function toCollectionItem(row: CollectionPieceRow): CollectionItem {
  return {
    slug: row.slug,
    name: row.name,
    photo: {
      src: publicPhotoUrl(row.image_path),
      width: row.image_width,
      height: row.image_height,
    },
    alt: row.alt,
    copy: row.summary,
    detail: row.detail || row.summary,
    includes: row.includes ?? [],
    craft: row.craft,
    occasion: row.occasion,
    featured: row.featured,
  };
}

export function collectionWithStudio(studio: CollectionItem[]) {
  const taken = new Set(COLLECTION.map((item) => item.slug));
  return [...studio.filter((item) => !taken.has(item.slug)), ...COLLECTION];
}

export function featuredWithStudio(studio: CollectionItem[]) {
  const extra = studio.filter((item) => item.featured && !RESERVED_SLUGS.has(item.slug));
  return [...extra, ...COLLECTION.filter((item) => item.featured)];
}

export function friendlyStudioError(error: { message?: string; code?: string } | null) {
  const message = error?.message ?? "";
  const code = error?.code ?? "";
  if (
    code === "42P01" ||
    code === "PGRST205" ||
    /collection_pieces|studio_allowlist|schema cache|does not exist/i.test(message)
  ) {
    return "The studio isn't connected to the database yet. Once that step is finished, this page will save pieces.";
  }
  if (/row-level security|permission denied|not authorized|42501/i.test(`${code} ${message}`)) {
    return "This login can view the studio, but it isn't allowed to change the collection.";
  }
  if (/payload too large|entity too large|maximum allowed size/i.test(message)) {
    return "That photo is too large. Use one under 12 MB.";
  }
  return message || "Something went wrong. Please try again.";
}

export type PhotoKind = "jpeg" | "png" | "webp" | "heic" | "other";

export function photoKind(file: File): PhotoKind {
  const type = file.type.toLowerCase();
  const name = file.name.toLowerCase();
  if (
    type === "image/heic" ||
    type === "image/heif" ||
    name.endsWith(".heic") ||
    name.endsWith(".heif")
  ) {
    return "heic";
  }
  if (type === "image/jpeg" || name.endsWith(".jpg") || name.endsWith(".jpeg")) return "jpeg";
  if (type === "image/png" || name.endsWith(".png")) return "png";
  if (type === "image/webp" || name.endsWith(".webp")) return "webp";
  return "other";
}

export function photoContentType(kind: Exclude<PhotoKind, "heic" | "other">) {
  if (kind === "jpeg") return "image/jpeg";
  if (kind === "png") return "image/png";
  return "image/webp";
}

export function readPhotoSize(file: File) {
  return new Promise<{ width: number; height: number }>((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      const width = image.naturalWidth || 1200;
      const height = image.naturalHeight || 1200;
      URL.revokeObjectURL(url);
      resolve({ width, height });
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not read that photo. Try a JPEG or PNG."));
    };
    image.src = url;
  });
}
